import { FirebaseError } from 'firebase/app';
import axios from 'axios';
import { formatFirestoreError } from './firestoreErrors';

export type UploadErrorKind =
  | 'invalid_format'
  | 'server_error'
  | 'firebase_error'
  | 'auth_error'
  | 'unknown';

export interface UploadErrorInfo {
  kind: UploadErrorKind;
  title: string;
  message: string;
  details?: string;
}

const ERROR_COPY: Record<UploadErrorKind, { title: string; message: string }> = {
  invalid_format: {
    title: "Couldn't Add Conversations",
    message: 'The file format is not supported or the conversation data is invalid.',
  },
  server_error: {
    title: "Couldn't Add Conversations",
    message: 'The server is unavailable or failed to process your upload.',
  },
  firebase_error: {
    title: "Couldn't Add Conversations",
    message: 'There was a problem saving conversations to your account.',
  },
  auth_error: {
    title: "Couldn't Add Conversations",
    message: 'You must be signed in to upload conversations.',
  },
  unknown: {
    title: "Couldn't Add Conversations",
    message: 'Something went wrong while uploading your conversations.',
  },
};

function buildError(kind: UploadErrorKind, details?: string): UploadErrorInfo {
  const copy = ERROR_COPY[kind];
  return {
    kind,
    title: copy.title,
    message: copy.message,
    details,
  };
}

function getAxiosDetail(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => (typeof item === 'string' ? item : item?.msg)).filter(Boolean).join(' ');
  }
  return undefined;
}

export function getUploadErrorInfo(error: unknown): UploadErrorInfo {
  if (error instanceof FirebaseError) {
    return buildError('firebase_error', formatFirestoreError(error));
  }

  if (axios.isAxiosError(error)) {
    const detail = getAxiosDetail(error);
    const status = error.response?.status;

    if (!error.response) {
      return buildError(
        'server_error',
        'Could not reach the backend. Make sure the API server is running and REACT_APP_API_URL is correct.',
      );
    }

    if (status === 401 || status === 403) {
      return buildError('auth_error', detail ?? 'Your session may have expired. Try signing in again.');
    }

    if (status === 400) {
      return buildError(
        'invalid_format',
        detail ?? 'The JSON file could not be parsed or does not match the expected conversation format.',
      );
    }

    if (status === 413) {
      return buildError('invalid_format', detail ?? 'The file is too large to upload.');
    }

    if (status === 504) {
      return buildError('server_error', detail ?? 'Upload timed out. Try a smaller file or split your export.');
    }

    if (status && status >= 500) {
      return buildError('server_error', detail ?? 'The server encountered an error while parsing your file.');
    }

    return buildError('unknown', detail ?? error.message);
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes('logged in') || message.includes('sign in')) {
      return buildError('auth_error', error.message);
    }

    if (
      message.includes('json') ||
      message.includes('format') ||
      message.includes('parse') ||
      message.includes('not iterable') ||
      message.includes('invalid conversation') ||
      message.includes('invalid server response')
    ) {
      return buildError('invalid_format', error.message);
    }

    if (message.includes('network') || message.includes('fetch')) {
      return buildError('server_error', error.message);
    }

    return buildError('unknown', error.message);
  }

  return buildError('unknown');
}

export function getInvalidFileTypeError(): UploadErrorInfo {
  return buildError(
    'invalid_format',
    'Please upload a .json file containing your exported conversation data.',
  );
}
